import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, CheckCircle, Clock, Users, RefreshCw, Layers, FolderOpen, UserCheck } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { apiService } from '@/services/api';
import type { Candidate } from '@/services/api';
// import { exportToCsv } from '@/services/dashboardApi'; // Comentado temporalmente

// Components
import KpiCard from '@/features/rh/components/dashboard/KpiCard';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { Revelar } from '@/shared/components/Revelar';
// import RequestsTable from '@/features/rh/components/dashboard/RequestsTable'; // Comentado temporalmente
// import UsersSummary from '@/features/rh/components/dashboard/UsersSummary'; // Comentado temporalmente
// import AuditActivity from '@/features/rh/components/dashboard/AuditActivity'; // Comentado temporalmente

const HRDashboard: React.FC = () => {
  // Filtros comentados temporalmente - no se usan en esta versión
  // const [dateFilter, setDateFilter] = useState('last30days');
  // const [statusFilter, setStatusFilter] = useState('all');

  const { user } = useAuth();
  const permissions = user?.rbac?.effectivePermissions || [];
  const has = (permission: string) => userHasPermission(permission, permissions);
  const hasDashboardFullAccess = has("rh.inicio.especial.read");
  const canViewRecruitmentPanel = has("rh.reclutamiento_candidatos.base.read");
  const canManageJobRequests = has("rh.gestion_solicitudes.base.read") || has("rh.solicitudes_personal.base.read");
  const canViewAdminByUsers =
    has("generales.catalogos.proyectos.read") ||
    has("generales.catalogos.roles.read") ||
    has("generales.catalogos.areas.read") ||
    has("generales.catalogos.puestos.read") ||
    has("proveedores.administracion.proveedores.consultar");
  const [candidatesInProcess, setCandidatesInProcess] = useState(0);
  const [isCandidatesLoading, setIsCandidatesLoading] = useState(false);

  const {
    data: apiData,
    isLoading,
    error,
    refetch
  } = useDashboardMetrics({ enabled: true }); // Siempre habilitado para obtener datos reales

  // Solo datos reales. Antes esto caia a un mock cuando la respuesta venia
  // vacia, asi que el dashboard pintaba metricas inventadas con cara de buenas
  // y ademas escondia que el API estaba fallando. Si no hay datos, las tarjetas
  // se quedan en su estado de carga o en cero, que es la verdad.
  const data = apiData;
  const priorityChartData = [
    { name: 'Baja', value: data?.jobRequests.priorityCounts.baja ?? 0 },
    { name: 'Media', value: data?.jobRequests.priorityCounts.media ?? 0 },
    { name: 'Alta', value: data?.jobRequests.priorityCounts.alta ?? 0 },
    { name: 'Urgente', value: data?.jobRequests.priorityCounts.urgente ?? 0 },
  ].filter((item) => item.value > 0);
  const statusChartData = [
    { name: 'Pendiente pre aprobación', value: data?.jobRequests.statusCounts.pendiente_pre_aprobacion ?? 0 },
    { name: 'Pre aprobada', value: data?.jobRequests.statusCounts.pre_aprobada ?? 0 },
    { name: 'Pendiente aprobación salario', value: data?.jobRequests.statusCounts.pendiente_aprobacion_salario ?? 0 },
    { name: 'En revisión', value: data?.jobRequests.statusCounts.en_revision ?? 0 },
    { name: 'Vacante activa', value: data?.jobRequests.statusCounts.vacante_activa ?? 0 },
    { name: 'Cubierta', value: data?.jobRequests.statusCounts.cubierta ?? 0 },
    { name: 'Rechazada', value: data?.jobRequests.statusCounts.rechazada ?? 0 },
  ].filter((item) => item.value > 0);

  /* Comentado temporalmente - no se usa cuando están ocultas las tablas
  const handleExportCsv = () => {
    if (data?.jobRequests.latestUpdates) {
      exportToCsv(data.jobRequests.latestUpdates, 'solicitudes_trabajo');
    }
  };
  */

  const loadCandidatesInProcess = useCallback(async () => {
    if (!canViewRecruitmentPanel) {
      setCandidatesInProcess(0);
      return;
    }

    try {
      setIsCandidatesLoading(true);
      const todas = await apiService.getApprovedJobRequests();
      // «Mi panel» cuenta solo lo propio: las vacantes que la persona solicitó.
      // Con el permiso del panel completo se cuenta todo, como antes.
      const jobs = hasDashboardFullAccess
        ? todas
        : todas.filter((job) => job.solicitante?.id === user?.id);
      const candidatesByJob = await Promise.all(
        jobs.map((job) => apiService.getCandidatesByJobRequest(job.id)),
      );
      const allCandidates = candidatesByJob.flat();
      const inProcessStatuses: Array<Candidate["status"]> = [
        "en_proceso",
        "entrevista_rh",
        "pre_seleccionado",
        "entrevista_solicitante",
      ];
      const totalInProcess = allCandidates.filter((candidate) =>
        inProcessStatuses.includes(candidate.status),
      ).length;
      setCandidatesInProcess(totalInProcess);
    } catch (err) {
      console.error("Error al cargar candidatos en proceso:", err);
      setCandidatesInProcess(0);
    } finally {
      setIsCandidatesLoading(false);
    }
  }, [canViewRecruitmentPanel, hasDashboardFullAccess, user?.id]);

  useEffect(() => {
    loadCandidatesInProcess();
  }, [loadCandidatesInProcess]);

  const handleRefresh = async () => {
    refetch();
    await loadCandidatesInProcess();
  };

  if (error && !apiData) {
    if (error.status === 401 || error.status === 403) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <PermissionsDenyPage />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center px-6">
        <h2 className="text-xl font-semibold text-gray-900">No se pudo cargar el dashboard</h2>
        <p className="text-sm text-gray-600 mt-2">
          {error.message || 'Ocurrió un problema al consultar las métricas. Intenta nuevamente.'}
        </p>
        <button
          onClick={handleRefresh}
          className={estiloDeBoton('primario', 'mt-4')}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto ml-2 px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <TituloDePantalla>
              {hasDashboardFullAccess ? "Panel administrativo" : "Mi panel"}
            </TituloDePantalla>
            <p className="text-sm text-gray-600 mt-1">
              {hasDashboardFullAccess
                ? "Monitoreo y análisis de solicitudes de trabajo y usuarios"
                : "Resumen de tus solicitudes y actividad"}
            </p>
          </div>

          <div className="mt-4 sm:mt-0 flex items-center space-x-3">

            {/* Botón Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className={estiloDeBoton('primario')}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'girando' : ''}`} />
              <span>
                {isLoading ? 'Actualizando...' : 'Actualizar'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* No son vacantes abiertas: `activeRequests` cuenta las solicitudes
            que siguen en el flujo de aprobación —pendiente de pre-aprobación,
            en revisión, devuelta, pre-aprobada, pendiente de salario y devuelta
            a RH—. Una vacante ya activa deja de contar aquí. */}
        <KpiCard
          title="Solicitudes en trámite"
          value={data?.jobRequests.totals.activeRequests ?? 0}
          icon={<Briefcase />}
          variant="purple"
          isLoading={isLoading}
        />
        <KpiCard
          title="Tiempo promedio de revisión"
          value={data?.jobRequests.totals.averageTimeToReviewHours ? `${Math.round(data.jobRequests.totals.averageTimeToReviewHours / 24)} días` : '0 días'}
          icon={<Clock />}
          variant="info"
          isLoading={isLoading}
        />
        <KpiCard
          title="Candidatos en proceso"
          value={candidatesInProcess}
          icon={<Users />}
          variant="secondary"
          isLoading={isLoading || isCandidatesLoading}
        />
        {/* Suma de plazas, no de solicitudes: una sola solicitud de tres
            plazas cuenta tres. */}
        <KpiCard
          title="Plazas abiertas"
          value={data?.jobRequests.totals.totalPositionsApproved ?? 0}
          icon={<CheckCircle />}
          variant="success"
          isLoading={isLoading}
        />
      </div>

      {/* Acciones rápidas */}
      {hasDashboardFullAccess && (canViewRecruitmentPanel || canManageJobRequests || canViewAdminByUsers) && (
        <Revelar className="mb-8">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Acciones rápidas</h3>
          <div className="flex flex-col md:flex-row gap-3">
            {canViewRecruitmentPanel && (
              <Link
                to="/rh-dashboard"
                className="flex-1 flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 no-underline hover:no-underline transition-all text-left"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#E0E7F5' }}
                >
                  <Layers className="w-5 h-5" style={{ color: '#315FB9' }} />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Ver panel de reclutamiento
                </span>
              </Link>
            )}

            {canManageJobRequests && (
              <Link
                to="/job-requests"
                className="flex-1 flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 no-underline hover:no-underline transition-all text-left"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#E0E7F5' }}
                >
                  <FolderOpen className="w-5 h-5" style={{ color: '#315FB9' }} />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Gestionar solicitudes de personal
                </span>
              </Link>
            )}

            {canViewAdminByUsers && (
              <Link
                to="/admin"
                className="flex-1 flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 no-underline hover:no-underline transition-all text-left"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#E0E7F5' }}
                >
                  <UserCheck className="w-5 h-5" style={{ color: '#315FB9' }} />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  Ver permisos por usuarios
                </span>
              </Link>
            )}
          </div>
        </div>
        </Revelar>
      )}

      {/* Charts Row */}
      {hasDashboardFullAccess && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Distribución de solicitudes por prioridad - Donut Chart */}
        <Revelar>
        <div className="bg--white rounded-lg shadow-sm border border-gray-200 p-6 h-full" style={{ backgroundColor: '#FFFFFF' }}>
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Distribución de solicitudes por prioridad</h3>
          {priorityChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={priorityChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    <Cell fill="#6CB4EE" />
                    <Cell fill="#4169E1" />
                    <Cell fill="#8B7EC8" />
                    <Cell fill="#1E3A8A" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                {priorityChartData.map((item, index) => {
                  const colors = ['#6CB4EE', '#4169E1', '#8B7EC8', '#1E3A8A'];
                  return (
                    <div key={item.name} className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[index % colors.length] }}></div>
                      <span className="text-sm text-gray-700">{item.name}</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
              Sin datos para mostrar
            </div>
          )}
        </div>
        </Revelar>

        {/* Solicitudes por estatus - Bar Chart */}
        <Revelar>
        <div className="bg--white rounded-lg shadow-sm border border-gray-200 p-6 h-full" style={{ backgroundColor: '#FFFFFF' }}>
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Solicitudes por estatus</h3>
          {statusChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={statusChartData}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  interval={0}
                  angle={0}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {statusChartData.map((item, index) => {
                    const colors = ['#FCD34D', '#6366F1', '#60A5FA', '#4ADE80', '#F87171', '#A78BFA', '#38BDF8'];
                    return <Cell key={`bar-${item.name}`} fill={colors[index % colors.length]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">
              Sin datos para mostrar
            </div>
          )}
        </div>
        </Revelar>
      </div>
      )}

      {/* Middle Section - Oculto temporalmente */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <RequestsTable
            requests={data?.jobRequests.latestUpdates || []}
            onExportCsv={handleExportCsv}
            isLoading={isLoading}
            showExportButton={true}
          />
        </div>
        
        <div>
          <UsersSummary
            totalUsers={data?.users.totalUsers || 0}
            activeUsers={data?.users.activeUsers || 0}
            inactiveUsers={data?.users.inactiveUsers || 0}
            newUsersLast30Days={data?.users.newUsersLast30Days || 0}
            recentUsers={data?.users.recent || []}
            isLoading={isLoading}
          />
        </div>
      </div> */}

      {/* Activity Section - Full Width - Oculto temporalmente */}
      {/* <div>
        <AuditActivity
          logs={data?.audit.recentActivity || []}
          moduleDistribution={data?.audit.moduleDistribution || {}}
          isLoading={isLoading}
        />
      </div> */}
    </div>
  );
};

export default HRDashboard;
